import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { PlannedPost } from "@/lib/api/interfaces/posts.interface";
import { usePostsGetPlannedInsights, usePostsDeletePlanned, usePostsBulkDeletePlanned } from "@/lib/api/requests/posts.requests";
import {
  DataView,
  DataViewLayout,
  DataViewContent,
  DataViewLoadingState,
  DataViewEmptyState,
  DataViewLoadMore,
  DataViewGridCard,
  DataViewListCard,
  DataViewToolbar,
  GenericItem,
  GenericItemActions,
  EntityPreviewFrame
} from "@/util-components/data-view";
import { PageInfoBanner, SubPageLayout } from "@/util-components/page-layout";
import { usePageState } from "@/lib/hooks/use-page-state";
import { CalendarClock, LayoutTemplate } from "lucide-react";
import { formatFriendlyDate } from "@/lib/utils/date-formatter";
import { getSocialIcon } from "@/page-components/post-generator/utils/socialUtils";
import { PlannedPostDialog } from "../components/PlannedPostDialog";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { toast } from "@/lib/toast";

const DEFAULT_PAGE_SIZE = 18;

const EmptyPreview = () => (
  <div className="flex h-full w-full items-center justify-center text-gray-300">
    <LayoutTemplate className="h-9 w-9" />
  </div>
);

const StatusLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[11px] font-medium text-gray-500">{children}</span>
);

const PlatformPill = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500">
    {children}
  </div>
);

const PlannedPosts: React.FC = () => {
  const navigate = useNavigate();
  const { state, setInfoBannerVisible, setViewMode } = usePageState('post-generator-planned-posts', {
    isInfoBannerVisible: true,
    viewMode: 'grid',
    settings: {}
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPosts, setAllPosts] = useState<PlannedPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<PlannedPost | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [postToDelete, setPostToDelete] = useState<PlannedPost | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  const deleteMutation = usePostsDeletePlanned();
  const bulkDeleteMutation = usePostsBulkDeletePlanned();
  
  const viewMode = state.viewMode || 'grid';

  const plannedQuery = usePostsGetPlannedInsights({
    page: currentPage - 1,
    limit: DEFAULT_PAGE_SIZE,
    q: searchTerm
  }, { enabled: true });

  useEffect(() => {
    if (plannedQuery.data?.docs) {
      if (currentPage === 1) {
        setAllPosts(plannedQuery.data.docs);
      } else {
        setAllPosts(prev => {
          const newPosts = plannedQuery.data.docs.filter((p: PlannedPost) => !prev.find(e => e._id === p._id));
          return [...prev, ...newPosts];
        });
      }
    }
  }, [plannedQuery.data, currentPage]);

  const hasMorePosts = plannedQuery.data?.hasNextPage || false;
  
  // Prevent empty state flash
  const isSyncing = (plannedQuery.data?.docs?.length ?? 0) > 0 && allPosts.length === 0;
  const isInitialLoading = (plannedQuery.isLoading || isSyncing) && currentPage === 1;
  const isLoadingMore = plannedQuery.isLoading && currentPage > 1;

  const handleViewModeChange = (newViewMode: 'grid' | 'list') => {
    setViewMode(newViewMode);
  };

  const handleSearchChange = (val: string) => {
    setSearchTerm(val);
    setCurrentPage(1);
    setAllPosts([]);
  };

  const handlePostClick = (postId: string) => {
    const post = allPosts.find(p => p._id === postId);
    if (post) {
      setSelectedPost(post);
      setShowDialog(true);
    }
  };

  const handleDelete = (postId: string) => {
    const post = allPosts.find(p => p._id === postId);
    if (post) {
      setPostToDelete(post);
      setIsBulkDelete(false);
      setShowDeleteDialog(true);
    }
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setPostToDelete(null);
      setIsBulkDelete(true);
      setShowDeleteDialog(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (isBulkDelete && selectedIds.length > 0) {
      bulkDeleteMutation.mutate(selectedIds, {
        onSuccess: () => {
          toast.success(`${selectedIds.length} geplante Posts erfolgreich gelöscht`);
          setCurrentPage(1);
          setAllPosts(prev => prev.filter(p => !selectedIds.includes(p._id)));
          setSelectedIds([]);
          setShowDeleteDialog(false);
          setIsBulkDelete(false);
          plannedQuery.refetch();
        }
      });
      return;
    }

    if (postToDelete) {
      deleteMutation.mutate(postToDelete._id, {
        onSuccess: () => {
          toast.success("Geplanter Post erfolgreich gelöscht");
          setCurrentPage(1);
          setAllPosts(prev => prev.filter(p => p._id !== postToDelete._id));
          setShowDeleteDialog(false);
          setPostToDelete(null);
          plannedQuery.refetch();
        }
      });
    }
  };


  const convertToGenericItem = (p: PlannedPost): GenericItem => {
    const isFailed = p.status === 'FAILED';
    const previewUrl = p.thumbnailUrl || p.thumbnailPath || p.template?.thumbnailUrl;

    return {
      id: p._id,
      title: p.message ? p.message.substring(0, 30) + (p.message.length > 30 ? '...' : '') : 'Geplanter Post',
      previewContent: (
        <EntityPreviewFrame backgroundUrl={previewUrl}>
          {(p.thumbnailUrl || p.thumbnailPath) ? (
            <img 
              src={p.thumbnailUrl || p.thumbnailPath} 
              alt={p.message || 'Geplanter Post'} 
              className="max-h-full max-w-full rounded-sm object-contain"
              loading="lazy"
            />
          ) : p.template?.thumbnailUrl ? (
            <img 
              src={p.template.thumbnailUrl} 
              alt={p.message || 'Geplanter Post'} 
              className="max-h-full max-w-full rounded-sm object-contain"
              loading="lazy"
            />
          ) : (
            <EmptyPreview />
          )}
        </EntityPreviewFrame>
      ),
      metadata: {
        badgeText: isFailed ? 'Fehler' : 'Geplant',
        footer: (
          <StatusLabel>{formatFriendlyDate(p.scheduledFor)}</StatusLabel>
        ),
        platforms: [
          (p.options?.instagramPost || p.options?.instagramStory) && (
            <PlatformPill key="ig">
              {getSocialIcon("INSTAGRAM", "h-3.5 w-3.5 text-gray-500")}
            </PlatformPill>
          ),
          (p.options?.facebookPost || p.options?.facebookStory) && (
            <PlatformPill key="fb">
              {getSocialIcon("FACEBOOK", "h-3.5 w-3.5 text-gray-500")}
            </PlatformPill>
          ),
          (p.options?.whatsappPost || (p.whatsappGroupIds && p.whatsappGroupIds.length > 0)) && (
            <PlatformPill key="wa">
              {getSocialIcon("WHATSAPP", "h-3.5 w-3.5 text-gray-500")}
            </PlatformPill>
          ),
          !(p.options?.instagramPost || p.options?.instagramStory || p.options?.facebookPost || p.options?.facebookStory || p.options?.whatsappPost || (p.whatsappGroupIds && p.whatsappGroupIds.length > 0)) && (
            <PlatformPill key="none">
              <LayoutTemplate className="w-3.5 h-3.5" />
            </PlatformPill>
          )
        ].filter(Boolean) as React.ReactNode[]
      }
    };
  };

  const genericItems = useMemo(() => {
    return allPosts.map(convertToGenericItem);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPosts]);

  const genericActions: GenericItemActions = {
    onItemClick: (id: string) => {
      handlePostClick(id);
    },
    onDelete: (id: string) => {
      handleDelete(id);
    }
  };

  const handleLoadMore = () => {
    if (hasMorePosts && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <SubPageLayout 
      pageTitle="Geplante Posts" 
      icon={<CalendarClock />} 
      navigateBackCallback={() => navigate(-1)}
    >
      <DataView 
        viewMode={viewMode} 
        onViewModeChange={handleViewModeChange}
        isLoading={isInitialLoading}
        isEmpty={allPosts.length === 0}
        selectedIds={selectedIds}
        onMultiSelect={setSelectedIds}
        onSelectionDelete={handleSelectionDelete}
      >
        <div className="bg-white flex-shrink-0 border-b border-gray-100">
          {state.isInfoBannerVisible && (
            <div className="p-4 pb-0">
              <PageInfoBanner
                title="Dein Content-Kalender"
                description="Hier siehst du alle Posts, die in Zukunft automatisch für dich veröffentlicht werden."
                icon={CalendarClock}
                variant="blue"
                onClose={() => setInfoBannerVisible(false)}
              />
            </div>
          )}
          
          <DataViewToolbar 
            searchQuery={searchTerm}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Posts durchsuchen..."
            infoText={`${plannedQuery.data?.count || 0} geplante Posts`}
          />
        </div>

        <DataViewLayout>
          <DataViewContent
            items={genericItems}
            renderGridItem={(item) => <DataViewGridCard item={item} actions={genericActions} />}
            renderListItem={(item) => <DataViewListCard item={item} actions={genericActions} />}
            loadingIndicator={<DataViewLoadingState />}
            emptyState={
              <DataViewEmptyState
                icon={<CalendarClock className="h-8 w-8 text-gray-400" />}
                title={searchTerm ? 'Keine Posts gefunden' : 'Keine geplanten Posts gefunden'}
                description={searchTerm ? 'Versuchen Sie andere Suchbegriffe.' : 'Du hast derzeit keine Inhalte geplant.'}
              />
            }
          />

          <DataViewLoadMore
            onLoadMore={handleLoadMore}
            isLoadingMore={isLoadingMore}
            hasMore={hasMorePosts}
          />
        </DataViewLayout>
        
        <PlannedPostDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          post={selectedPost}
        />

        {(showDeleteDialog && (postToDelete || isBulkDelete)) && (
          <StandardDeleteDialog
              open={showDeleteDialog}
              onOpenChange={setShowDeleteDialog}
              title={isBulkDelete ? `${selectedIds.length} geplante Posts löschen` : "Geplanten Post löschen"}
              description={isBulkDelete ? "Möchten Sie diese geplanten Posts wirklich löschen?" : "Möchten Sie diesen geplanten Post wirklich löschen?"}
              itemTitle={postToDelete?.message || (postToDelete?.template as any)?.name || "Post"}
              itemsList={isBulkDelete ? selectedIds.map(id => {
                const p = allPosts.find(p => p._id === id);
                return {
                  title: p ? (p.message || (p.template as any)?.name || 'Geplanter Post') : id,
                  subtitle: p ? formatFriendlyDate(p.scheduledFor) : undefined
                };
              }) : undefined}
              onDelete={handleDeleteConfirm}
              isLoading={deleteMutation.isPending || bulkDeleteMutation.isPending}
          />
        )}
      </DataView>
    </SubPageLayout>
  );
};

export default PlannedPosts;
